import { useAuth } from "@workos-inc/authkit-react";
import { useMemo } from "react";
import { type EntryFilterOption, useEntryFiltersCache } from "@/shared/cache";
import { needsTimekeeperSelection } from "./filters/timekeeperOptions";
import type { Entry } from "./normalize";

export type Timekeeper = {
	id: string;
	firstName: string;
	lastName: string;
	fullName?: string;
	email?: string;
};

function splitName(fullName: string): { firstName: string; lastName: string } {
	const parts = fullName.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return { firstName: "", lastName: "" };
	if (parts.length === 1) return { firstName: parts[0], lastName: parts[0] };
	return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

function fromFilterOption(option: EntryFilterOption): Timekeeper {
	const { firstName, lastName } = splitName(option.label);
	return {
		id: option.value,
		firstName,
		lastName,
		fullName: option.label,
		email: option.sublabel,
	};
}

/**
 * Returns `null` only for an empty list or the current user alone. Solo
 * users keep the Entries page focused on their own entries, while delegates get
 * the timekeeper column/filter sourced from `GET /v2/entry-groups/filters`.
 */
export function useConfiguredTimekeepers(): readonly Timekeeper[] | null {
	const { user } = useAuth();
	const { entryFilters } = useEntryFiltersCache();
	return useMemo(() => {
		return needsTimekeeperSelection(entryFilters.timekeeper, user?.email)
			? entryFilters.timekeeper.map(fromFilterOption)
			: null;
	}, [entryFilters.timekeeper, user?.email]);
}

export function formatTimekeeperName(timekeeper: Timekeeper): string {
	const firstInitial = timekeeper.firstName.trim().charAt(0).toUpperCase();
	const lastName = timekeeper.lastName.trim();
	if (!firstInitial) return lastName || timekeeper.fullName || timekeeper.id;
	if (!lastName) return firstInitial;
	return `${firstInitial}. ${lastName}`;
}

export function formatTimekeeperFullName(timekeeper: Timekeeper): string {
	const fullName = timekeeper.fullName?.trim();
	if (fullName) return fullName;
	const firstName = timekeeper.firstName.trim();
	const lastName = timekeeper.lastName.trim();
	if (!firstName) return lastName || timekeeper.id;
	if (!lastName || firstName === lastName) return firstName;
	return `${firstName} ${lastName}`;
}

export function findTimekeeper(
	timekeepers: readonly Timekeeper[] | null | undefined,
	id: string | null | undefined,
): Timekeeper | null {
	if (!timekeepers || !id) return null;
	return timekeepers.find((timekeeper) => timekeeper.id === id) ?? null;
}

export function getTimekeeperId(entry: Entry): string | null {
	return entry.timekeeperId ?? null;
}

export function getEntryTimekeeperLabel(
	entry: Entry,
	timekeepers: readonly Timekeeper[] | null | undefined,
): string | null {
	const id = getTimekeeperId(entry);
	const configured = findTimekeeper(timekeepers, id);
	if (configured) return formatTimekeeperName(configured);
	return null;
}
